// @ts-nocheck
/**
 * カード価格履歴フック
 *
 * サーバーから価格履歴を取得し、リアルタイム更新をサポート
 *
 * 特徴:
 * - 3階層OHLCデータ（15分/日次/週次）の統合取得
 * - キャッシュ機構による効率的なデータ管理
 * - 既存のSupabase Realtimeソケットを活用した自動更新
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useGlobalMarket } from '../../lib/useGlobalMarket';

// ========================================
// 型定義
// ========================================

/** OHLC（始値・高値・安値・終値）データ */
export interface OHLCData {
  period: Date;
  open: number;
  high: number;
  low: number;
  close: number;
  granularity: '15min' | 'daily' | 'weekly';
}

/** 期間タイプ */
export type PeriodType = '24h' | '7d' | '30d' | '90d';

/** 価格履歴統計 */
export interface PriceHistoryStats {
  currentPrice: number;
  periodHigh: number;
  periodLow: number;
  changePercent: number;
  changeAmount: number;
}

/** フックの戻り値 */
export interface UseCardPriceHistoryReturn {
  data: OHLCData[] | null;
  stats: PriceHistoryStats | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  lastUpdated: Date | null;
  granularity: ('15min' | 'daily' | 'weekly')[];
}

/** 期間設定 */
export const PERIOD_CONFIG: Record<PeriodType, {
  label: string;
  shortLabel: string;
  days: number;
  expectedGranularity: ('15min' | 'daily' | 'weekly')[];
}> = {
  '24h': { label: '24時間', shortLabel: '24h', days: 1, expectedGranularity: ['15min'] },
  '7d': { label: '7日間', shortLabel: '7d', days: 7, expectedGranularity: ['15min', 'daily'] },
  '30d': { label: '30日間', shortLabel: '30d', days: 30, expectedGranularity: ['daily'] },
  '90d': { label: '90日間', shortLabel: '90d', days: 90, expectedGranularity: ['daily', 'weekly'] },
};

// ========================================
// キャッシュ管理
// ========================================

interface CacheEntry {
  data: OHLCData[];
  fetchedAt: Date;
  lastServerTimestamp: Date;
}

interface PriceHistoryCache {
  [cardId: string]: {
    [period in PeriodType]?: CacheEntry;
  };
}

/** キャッシュ有効期限（ミリ秒） */
const CACHE_TTL: Record<PeriodType, number> = {
  '24h': 5 * 60 * 1000,    // 5分
  '7d': 30 * 60 * 1000,    // 30分
  '30d': 60 * 60 * 1000,   // 1時間
  '90d': 60 * 60 * 1000,   // 1時間
};

// モジュールスコープのキャッシュ
const priceHistoryCache: PriceHistoryCache = {};

/** キャッシュをクリア */
export function clearPriceHistoryCache(cardId?: string): void {
  if (cardId) {
    delete priceHistoryCache[cardId];
  } else {
    Object.keys(priceHistoryCache).forEach(key => {
      delete priceHistoryCache[key];
    });
  }
}

// ========================================
// メインフック
// ========================================

/**
 * カード価格履歴を取得・管理するフック
 *
 * @param cardId - カードID
 * @param period - 期間（'24h' | '7d' | '30d' | '90d'）
 * @param options - オプション
 * @returns 価格履歴データと状態
 */
export function useCardPriceHistory(
  cardId: string | null,
  period: PeriodType = '24h',
  options: {
    autoRefresh?: boolean;
    rarityMultiplier?: number;
  } = {}
): UseCardPriceHistoryReturn {
  const { autoRefresh = true, rarityMultiplier = 1 } = options;

  const globalMarket = useGlobalMarket();
  const [data, setData] = useState<OHLCData[] | null>(null);
  const [stats, setStats] = useState<PriceHistoryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // refで関数を保持（無限ループ防止）
  const getUnifiedPriceHistoryRef = useRef(globalMarket.getUnifiedPriceHistory);
  const getPriceRef = useRef(globalMarket.getPrice);
  const lastPriceRef = useRef<number | null>(null);
  const lastTimestampRef = useRef<Date | null>(null);
  const isFetchingRef = useRef(false);

  // refを更新
  useEffect(() => {
    getUnifiedPriceHistoryRef.current = globalMarket.getUnifiedPriceHistory;
    getPriceRef.current = globalMarket.getPrice;
  }, [globalMarket.getUnifiedPriceHistory, globalMarket.getPrice]);

  // 統計値を計算
  const calculateStats = useCallback((ohlcData: OHLCData[], multiplier: number): PriceHistoryStats => {
    if (ohlcData.length === 0) {
      return {
        currentPrice: 0,
        periodHigh: 0,
        periodLow: 0,
        changePercent: 0,
        changeAmount: 0,
      };
    }

    const currentPrice = ohlcData[ohlcData.length - 1].close * multiplier;
    const openPrice = ohlcData[0].open * multiplier;
    const periodHigh = Math.max(...ohlcData.map(d => d.high)) * multiplier;
    const periodLow = Math.min(...ohlcData.map(d => d.low)) * multiplier;
    const changeAmount = currentPrice - openPrice;
    const changePercent = openPrice > 0 ? Math.round((changeAmount / openPrice) * 1000) / 10 : 0;

    return {
      currentPrice: Math.round(currentPrice),
      periodHigh: Math.round(periodHigh),
      periodLow: Math.round(periodLow),
      changePercent,
      changeAmount: Math.round(changeAmount),
    };
  }, []);

  // サーバーから履歴を取得
  const fetchHistory = useCallback(async () => {
    if (!cardId) {
      setData(null);
      setStats(null);
      return;
    }

    // 既にフェッチ中なら何もしない
    if (isFetchingRef.current) {
      return;
    }

    const now = new Date();

    // キャッシュ確認
    const cached = priceHistoryCache[cardId]?.[period];
    if (cached && (now.getTime() - cached.fetchedAt.getTime()) < CACHE_TTL[period]) {
      setData(cached.data);
      setStats(calculateStats(cached.data, rarityMultiplier));
      setLastUpdated(cached.fetchedAt);
      lastPriceRef.current = cached.data[cached.data.length - 1]?.close || null;
      lastTimestampRef.current = cached.lastServerTimestamp;
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const config = PERIOD_CONFIG[period];
      const historyData = await getUnifiedPriceHistoryRef.current(cardId, config.days);

      // データを昇順にソート（古い順）
      const sortedData = [...historyData].sort(
        (a, b) => new Date(a.period).getTime() - new Date(b.period).getTime()
      );

      if (sortedData.length === 0) {
        setData([]);
        setStats(null);
        setError(null);
      } else {
        // キャッシュに保存
        if (!priceHistoryCache[cardId]) {
          priceHistoryCache[cardId] = {};
        }
        priceHistoryCache[cardId][period] = {
          data: sortedData,
          fetchedAt: now,
          lastServerTimestamp: now,
        };

        setData(sortedData);
        setStats(calculateStats(sortedData, rarityMultiplier));
        lastPriceRef.current = sortedData[sortedData.length - 1]?.close || null;
        lastTimestampRef.current = now;
      }

      setLastUpdated(now);
    } catch (err) {
      console.error('Failed to fetch price history:', err);
      setError(err instanceof Error ? err.message : '価格履歴の取得に失敗しました');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [cardId, period, calculateStats, rarityMultiplier]);

  // 初回ロードと期間変更時に実行
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // リアルタイム更新（グローバル市場の価格変更を検知）
  // タイムスタンプの変更のみを監視
  const serverTimestamp = globalMarket.timestamp?.getTime();

  useEffect(() => {
    if (!autoRefresh || !cardId || !serverTimestamp || period !== '24h') {
      return;
    }

    // タイムスタンプが更新されたかチェック
    if (lastTimestampRef.current &&
        serverTimestamp <= lastTimestampRef.current.getTime()) {
      return;
    }

    const newPrice = getPriceRef.current(cardId);
    if (!newPrice || newPrice === 0) {
      return;
    }

    // 新しい価格データをキャッシュに追加
    const cached = priceHistoryCache[cardId]?.[period];
    if (cached && lastPriceRef.current !== null && cached.data.length > 0) {
      const newTimestamp = new Date(serverTimestamp);
      const newOHLC: OHLCData = {
        period: newTimestamp,
        open: lastPriceRef.current,
        high: Math.max(lastPriceRef.current, newPrice),
        low: Math.min(lastPriceRef.current, newPrice),
        close: newPrice,
        granularity: '15min',
      };

      // 末尾に追加（最大96データポイント = 24時間分）
      const updatedData = [...cached.data, newOHLC].slice(-96);
      cached.data = updatedData;
      cached.lastServerTimestamp = newTimestamp;
      cached.fetchedAt = new Date();

      setData(updatedData);
      setStats(calculateStats(updatedData, rarityMultiplier));
      setLastUpdated(newTimestamp);

      lastPriceRef.current = newPrice;
      lastTimestampRef.current = newTimestamp;
    }
  }, [autoRefresh, cardId, period, serverTimestamp, calculateStats, rarityMultiplier]);

  // 粒度の一覧を取得
  const granularity = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...new Set(data.map(d => d.granularity))];
  }, [data]);

  return {
    data,
    stats,
    loading,
    error,
    refetch: fetchHistory,
    lastUpdated,
    granularity,
  };
}

export default useCardPriceHistory;
